import { Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth, ROLE_LABELS, isSuperAdmin } from "../../lib/auth";
import { LOGO_URL } from "../../lib/api";
import { LayoutDashboard, Users, Megaphone, HandHeart, Network, LogOut, ExternalLink, Gauge, FolderOpen, Inbox, BarChart3, ClipboardList, ChevronDown, UserCircle, Settings, HeartHandshake, Newspaper } from "lucide-react";

// LayoutDashboard reserved for future "Reports" nav item — kept to avoid churn when added back.
void LayoutDashboard;

const NAV = [
  { to: "/admin", label: "Dashboard", icon: Gauge, end: true },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/donations", label: "Donations", icon: HeartHandshake },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox },
  { to: "/admin/sponsors", label: "Sponsors", icon: HandHeart },
  { to: "/admin/partners", label: "Partners", icon: Network },
  { to: "/admin/news", label: "News", icon: Megaphone },
  { to: "/admin/documents", label: "Documents", icon: FolderOpen },
  { to: "/admin/media", label: "Media Room", icon: Newspaper },
  { to: "/admin/run-of-show", label: "Run of Show", icon: ClipboardList },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex">
      {/* Sidebar */}
      <aside data-testid="admin-sidebar" className="w-64 shrink-0 border-r border-white/10 bg-aawta-charcoal/50 flex flex-col">
        <div className="p-6 border-b border-white/10 flex items-center gap-3">
          <img src={LOGO_URL} alt="AAWTA" className="h-10 w-10 object-contain bg-white/95 rounded-full p-1" />
          <div>
            <p className="text-overline text-aawta-pink leading-tight">AAWTA</p>
            <p className="font-display text-sm text-white leading-tight">Command Center</p>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-all border-l-2 ${
                  active
                    ? "border-aawta-pink bg-aawta-pink/10 text-white"
                    : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon size={16} /> {n.label}
              </Link>
            );
          })}
          {isSuperAdmin(user) && (
            <Link
              to="/admin/users"
              data-testid="nav-users"
              className={`flex items-center gap-3 px-3 py-2.5 text-sm transition-all border-l-2 ${
                loc.pathname.startsWith("/admin/users")
                  ? "border-aawta-pink bg-aawta-pink/10 text-white"
                  : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
              }`}
            >
              <Users size={16} /> Users
            </Link>
          )}
        </nav>
        <div className="p-4 border-t border-white/10">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-overline text-white/40 hover:text-aawta-pink py-2 transition-colors"
          >
            <ExternalLink size={11} /> VIEW PUBLIC SITE
          </a>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col min-w-0">
        <header data-testid="admin-topbar" className="border-b border-white/10 px-8 py-4 flex items-center justify-between">
          <div className="text-overline text-white/40">AAWTA BACKOFFICE</div>
          <UserMenu user={user} logout={logout} />
        </header>
        <div className="flex-1 p-8 overflow-y-auto">{children}</div>
      </main>
    </div>
  );
}


function UserMenu({ user, logout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user) return null;
  const initials = (user.name || user.email || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="user-menu-btn"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-3 hover:opacity-90 transition-opacity"
      >
        {user.picture ? (
          <img src={user.picture} alt={user.name || user.email} className="h-9 w-9 rounded-full object-cover border border-aawta-pink/40" />
        ) : (
          <div className="h-9 w-9 rounded-full bg-aawta-pink/20 border border-aawta-pink/40 flex items-center justify-center text-aawta-pink text-xs font-mono">{initials}</div>
        )}
        <div className="text-left hidden sm:block">
          <p className="text-sm text-white leading-tight" data-testid="user-menu-name">{user.name || "Admin"}</p>
          <p className="text-overline text-aawta-pink leading-tight" data-testid="user-menu-role">{ROLE_LABELS[user.role] || "—"}</p>
        </div>
        <ChevronDown size={14} className={`text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div data-testid="user-menu-dropdown" className="absolute right-0 mt-3 w-64 border border-white/10 bg-[#0F0F0F] shadow-2xl z-50">
          <div className="px-4 py-3 border-b border-white/5">
            <p className="text-sm text-white">{user.name || "Admin"}</p>
            <p className="text-xs text-white/50">{user.email}</p>
          </div>
          <Link to="/admin/profile" data-testid="user-menu-profile" onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-aawta-pink/10 transition-colors">
            <UserCircle size={14} className="text-aawta-pink" /> Profile
          </Link>
          {isSuperAdmin(user) && (
            <Link to="/admin/users" data-testid="user-menu-settings" onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-aawta-pink/10 transition-colors">
              <Settings size={14} className="text-aawta-pink" /> Settings
            </Link>
          )}
          <a href="/" target="_blank" rel="noopener noreferrer"
             className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-aawta-pink/10 transition-colors border-t border-white/5">
            <ExternalLink size={14} className="text-aawta-pink" /> View Public Site
          </a>
          <button data-testid="logout-btn" onClick={logout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:text-white hover:bg-red-500/10 transition-colors border-t border-white/5">
            <LogOut size={14} className="text-red-400" /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
